<template>
  <div class="prepeers-input">
    <label
      v-if="props.label"
      :for="inputId"
      class="prepeers-input__label">
      {{ props.label }}
    </label>

    <input
      :id="inputId"
      class="prepeers-input__field"
      :class="{ 'prepeers-input__field--error': !!props.error }"
      :type="props.type"
      :value="props.modelValue"
      :placeholder="props.placeholder"
      :required="props.required"
      :autocomplete="props.autocomplete"
      :name="props.name"
      :disabled="props.disabled"
      :minlength="props.minlength"
      :enterkeyhint="props.enterKeyHint"
      @input="onInput" />

    <p
      v-if="props.error"
      class="prepeers-input__error">
      {{ props.error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'

type InputType = 'text' | 'email' | 'password' | 'tel' | 'number' | 'search' | 'url'

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  label: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  placeholder: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  type: {
    type: String as PropType<InputType>,
    default: 'text',
  },
  error: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  name: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  minlength: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
  enterKeyHint: {
    type: String as PropType<
      'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send' | undefined
    >,
    default: undefined,
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const inputId: ComputedRef<string> = computed<string>(
  () => props.id ?? `prepeers-input-${Math.random().toString(36).slice(2, 7)}`,
)

/**
 * Propage la valeur saisie au parent via v-model.
 * @param {Event} event - Événement DOM natif.
 */
function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}
</script>

<style scoped>
.prepeers-input {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.prepeers-input__label {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
}

.prepeers-input__field {
  width: 100%;
  padding: 0.7em 1.1em;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
  outline: none;
}

.prepeers-input__field::placeholder {
  color: var(--text-muted);
}

.prepeers-input__field:focus {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}

.prepeers-input__field--error {
  border-color: var(--color-error);
}

.prepeers-input__field--error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.prepeers-input__field:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prepeers-input__error {
  font-size: var(--text-xs);
  color: var(--color-error);
  line-height: var(--leading-snug);
}

/* iOS : évite le zoom au focus si font-size < 16px */
@media (max-width: 1024px) {
  .prepeers-input__field {
    font-size: 16px;
  }
}
</style>
